import { Button, EmptyState, useAnimatedNative } from "../../mads";
import { useI18n } from "../../shared/I18n";
import { LoadingIndicator } from "./components/core/LoadingIndicator/LoadingIndicator";
import { NotFoundErrorPage } from "./components/core/NotFoundErrorPage/NotFoundErrorPage";

import React, { useEffect, useRef, useState } from "react";
import { Animated, StyleSheet } from "react-native";

export type LoadingStatus = "error" | "success" | "idle" | "active" | "fatal";

const MINIMUM_LOADING_TIME = 300; // Reduced minimum time to show loading indicator
const CROSSFADE_DURATION = 350; // Duration for loading fade out + content fade in

export function ViewJobLoadingState({
  status,
  errorMesssage,
  onRetry,
  url,
  children,
  wasPreloaded = false,
}: {
  status: LoadingStatus;
  errorMesssage?: string;
  onRetry: () => void;
  /**
   * @deprecated The url appears to be unused in the error page.
   * @see https://indeed.atlassian.net/browse/WENG-1354
   */
  url: string | null;
  /**
   * The children to render when the status is success.
   */
  children: React.ReactNode;
  wasPreloaded?: boolean;
}) {
  const skipShimmer = wasPreloaded;
  const { gettext } = useI18n();
  const loadingOpacityAnim = useRef(new Animated.Value(skipShimmer ? 0 : 1)).current;
  const contentFadeAnim = useRef(new Animated.Value(skipShimmer ? 1 : 0)).current;
  const [showLoadingState, setShowLoading] = useState(!skipShimmer);
  const [contentReadyState, setContentReady] = useState(skipShimmer);
  const [hasStartedTransition, setHasStartedTransition] = useState(false);

  // Derived during render rather than read straight off state: `renderToString` never
  // runs effects, so content gated only on effect-set state is omitted from the SSR
  // HTML entirely. When the data is already available at first render (`skipShimmer`),
  // the content must be in that first render pass on both the server and the first
  // hydration render.
  const contentReady = contentReadyState || skipShimmer;
  const showLoading = showLoadingState && !skipShimmer;
  const loadingStartTime = useRef<number | null>(null);
  const useNativeDriver = useAnimatedNative();

  useEffect(() => {
    // Start the loading timer on the first loading status we encounter
    // - GraphQL starts with 'active' status
    // - REST starts with 'idle' status
    const isLoadingStatus = status === "idle" || status === "active";

    // Only set timer if it hasn't been set yet (loadingStartTime is null)
    if (isLoadingStatus && loadingStartTime.current === null) {
      loadingStartTime.current = Date.now();
    }

    if (status === "success" && !hasStartedTransition) {
      const now = Date.now();
      const elapsedTime = loadingStartTime.current ? now - loadingStartTime.current : 0;
      const remainingTime = Math.max(0, MINIMUM_LOADING_TIME - elapsedTime);

      setHasStartedTransition(true);

      // Two-stage animation: prepare content → crossfade
      Animated.sequence([Animated.delay(remainingTime)]).start(() => {
        // Render content and start crossfade immediately
        setContentReady(true);

        // Pure cross-dissolve: loading fades out while content fades in
        Animated.parallel([
          Animated.timing(loadingOpacityAnim, {
            toValue: 0,
            duration: CROSSFADE_DURATION,
            useNativeDriver,
          }),
          Animated.timing(contentFadeAnim, {
            toValue: 1,
            duration: CROSSFADE_DURATION,
            useNativeDriver,
          }),
        ]).start(() => {
          // Clean up loading indicator after crossfade
          setShowLoading(false);
        });
      });
    } else if (status !== "success" && status !== "idle" && status !== "active") {
      // Reset animation state if we go to error states
      setShowLoading(true);
      setContentReady(false);
      setHasStartedTransition(false);
      loadingStartTime.current = null;
      loadingOpacityAnim.setValue(1);
      contentFadeAnim.setValue(0);
    }
  }, [status, loadingOpacityAnim, contentFadeAnim, hasStartedTransition, useNativeDriver]);

  if (status === "error" || status === "fatal") {
    // OneGraph turns JRS 404s into HTTP 200 with empty data, so a
    // user-not-found case never reaches this branch with an error message.
    // If we have *any* concrete message (Apollo network/HTTP errors, native
    // module failures, 5xx propagated from JRS, etc.), treat it as a real
    // failure and offer a retry. Fall back to NotFoundErrorPage only when
    // no message is present, which mirrors the legacy "OneGraph swallowed
    // it" behavior.
    if (errorMesssage) {
      return (
        <EmptyState
          title={gettext("Something went wrong on our end")}
          description={gettext("We’re working to fix it. Please try again later.")}
          actions={<Button onPress={onRetry}>{gettext("Retry")}</Button>}
        />
      );
    }
    return <NotFoundErrorPage url={url} />;
  }

  return (
    <>
      {/* Loading indicator - fades out during cross-dissolve */}
      {showLoading && (
        <Animated.View
          style={[styles.loadingOverlay, { opacity: loadingOpacityAnim }]}
          pointerEvents="none"
        >
          <LoadingIndicator />
        </Animated.View>
      )}

      {/* Content - fades in during cross-dissolve */}
      {contentReady && (
        // `contentFadeAnim` is seeded once at mount, so when `skipShimmer` only becomes
        // true later (cold load, data arrives) it is still 0 while the shimmer is already
        // hidden — the content would render fully transparent. Skipping the shimmer means
        // there is nothing to cross-dissolve from, so show the content outright.
        <Animated.View style={[styles.content, { opacity: skipShimmer ? 1 : contentFadeAnim }]}>
          {children}
        </Animated.View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  content: {
    display: "contents",
  },
});
